// Admin → Catalogue → Supplier Import.
//
// A reusable, UI-driven catalogue import pipeline (replaces per-supplier import
// scripts). Four steps, backed by /api/admin/catalogue:
//
//   1 Upload    pick/create a supplier, drop a CSV/XLSX → POST /import/upload
//               parses headers + a 5-row preview and stages the file (file_ref).
//               A saved mapping for the supplier is returned and pre-fills step 2.
//   2 Map       map each canonical TKOS field → a CSV column (or skip). Required
//               fields must be mapped; a column mapped twice is flagged.
//   3 Validate  POST /import/validate cleans prices (strips the PWS ¬ artifact)
//               and reports row-level issues + counts before committing.
//   4 Import    POST /import/run upserts into catalogue_products by
//               (supplier_id, product_code), writes the audit log, and — only if
//               opted in — soft-removes products absent from the file.
//
// Reached from the sidebar (catalogue cluster) — see shell/pages-registry.ts.

import { api } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

interface SupplierOpt { id: string; slug: string; name: string; }
interface FieldDef { key: string; label: string; required: boolean; price?: boolean; }

interface UploadResp {
  ok: boolean;
  file_ref: string;
  filename: string;
  kind: "csv" | "xlsx";
  headers: string[];
  header_row: number;
  skipped_title: boolean;
  total_rows: number;
  preview_rows: string[][];
  saved_mapping: Record<string, string> | null;
}

interface Issue { row: number; field: string; value: string; issue: string; severity: "warning" | "error"; }
interface ValidateCounts {
  total_rows: number; valid_rows: number; ready_to_import: number;
  warning_rows: number; error_rows: number; skipped_rows: number; duplicate_codes: number;
  cleaned_price_cells: number;
}
interface SampleRow {
  product_code: string; description: string; range: string;
  list_price: number | null; net_price: number | null; pto_price: number | null; discount_code: string | null;
}
interface ValidateResp {
  ok: boolean; supplier: { id: string; name: string }; filename: string;
  counts: ValidateCounts; issues: Issue[]; sample: SampleRow[];
}

interface RunCounts {
  total_rows: number; imported_rows: number; inserted_rows: number;
  updated_rows: number; error_rows: number; deactivated_rows: number;
}
interface RunResp {
  ok: boolean; supplier: { id: string; name: string }; log_id: string;
  counts: RunCounts; status: "success" | "partial" | "failed"; duration_ms: number;
  errors?: { row: number; reason: string }[];
}

const SKIP = "";          // sentinel for "Skip this column"
const MAX_FILE_BYTES = 60 * 1024 * 1024;
const STEPS = ["Upload", "Map", "Validate", "Import"] as const;

// Fallback field list (also fetched from the server so the two never drift).
const FALLBACK_FIELDS: FieldDef[] = [
  { key: "product_code", label: "Product Code", required: true },
  { key: "description", label: "Description", required: true },
  { key: "category", label: "Category", required: true },
  { key: "range", label: "Range", required: true },
  { key: "range_family", label: "Range Family", required: false },
  { key: "colour_finish", label: "Colour / Finish", required: false },
  { key: "room", label: "Room", required: false },
  { key: "list_price", label: "List Price", required: true, price: true },
  { key: "net_price", label: "Net Price", required: false, price: true },
  { key: "pto_price", label: "PTO Price", required: false, price: true },
  { key: "box_qty", label: "Box Qty", required: false },
  { key: "discount_code", label: "Discount Code", required: false },
  { key: "directory_section", label: "Directory Section", required: false },
  { key: "unit_of_measure", label: "Unit of Measure", required: false },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text != null) node.textContent = text;
  return node;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function gbp(v: number | null): string {
  if (v == null) return "—";
  return `£${v.toFixed(2)}`;
}

async function errMessage(res: Response): Promise<string> {
  const txt = await res.text().catch(() => "");
  try { const j = JSON.parse(txt); if (j.error) return j.error; } catch { /* keep text */ }
  return txt || `HTTP ${res.status}`;
}

// ── Page ─────────────────────────────────────────────────────────────────────

const page: Page = {
  name: "catalogue-supplier-import",
  label: "Supplier Import",
  icon: "📥",
  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    // ── State ──────────────────────────────────────────────────────────────
    let fields: FieldDef[] = FALLBACK_FIELDS;
    let suppliers: SupplierOpt[] = [];
    let supplierId = "";
    let addingSupplier = false;
    let creatingSupplier = false;

    let file: File | null = null;
    let uploading = false;
    let upload: UploadResp | null = null;

    let mapping: Record<string, string> = {};
    let saveMapping = true;

    let validating = false;
    let validation: ValidateResp | null = null;

    let deactivateAbsent = false;
    let running = false;
    let runResult: RunResp | null = null;

    let step = 1;          // 1..4
    let maxStep = 1;       // furthest reached

    const ac = new AbortController();

    function requiredKeys(): string[] { return fields.filter((f) => f.required).map((f) => f.key); }

    function requiredMapped(): boolean {
      return requiredKeys().every((k) => (mapping[k] || "").trim() !== "");
    }

    // CSV columns mapped to more than one TKOS field (a likely mistake).
    function duplicateColumns(): string[] {
      const counts = new Map<string, number>();
      for (const k of Object.keys(mapping)) {
        const v = (mapping[k] || "").trim();
        if (v) counts.set(v, (counts.get(v) || 0) + 1);
      }
      return [...counts.entries()].filter(([, n]) => n > 1).map(([col]) => col);
    }

    // Invalidate everything downstream of a mapping/file change.
    function invalidateFromMapping(): void { validation = null; runResult = null; }
    function invalidateFromUpload(): void { mapping = {}; invalidateFromMapping(); }

    function goStep(n: number): void {
      step = n;
      if (n > maxStep) maxStep = n;
      render();
    }

    // ── Render ──────────────────────────────────────────────────────────────
    function render(): void {
      container.replaceChildren();
      const wrap = el("section", "aci-wrap");

      const head = el("header", "aci-head");
      head.appendChild(el("h1", undefined, "Supplier Catalogue Import"));
      head.appendChild(el("p", "aci-sub",
        "Upload a supplier price list (CSV or Excel), map its columns once, and import. The mapping is saved per supplier — repeat uploads need no remapping."));
      wrap.appendChild(head);

      wrap.appendChild(renderStepBar());

      if (step === 1) wrap.appendChild(renderUpload());
      else if (step === 2) wrap.appendChild(renderMap());
      else if (step === 3) wrap.appendChild(renderValidate());
      else if (step === 4) wrap.appendChild(renderImport());

      container.appendChild(wrap);
    }

    function renderStepBar(): HTMLElement {
      const bar = el("div", "aci-steps");
      STEPS.forEach((label, i) => {
        const n = i + 1;
        const item = el("button", "aci-step");
        item.type = "button";
        if (n === step) item.classList.add("is-active");
        if (n < step) item.classList.add("is-done");
        item.disabled = n > maxStep;
        item.appendChild(el("span", "aci-step-num", n < step ? "✓" : String(n)));
        item.appendChild(el("span", "aci-step-label", label));
        item.addEventListener("click", () => { if (n <= maxStep) goStep(n); });
        bar.appendChild(item);
        if (n < STEPS.length) bar.appendChild(el("span", "aci-step-sep"));
      });
      return bar;
    }

    // ── Step 1 — Upload ──────────────────────────────────────────────────────
    function renderUpload(): HTMLElement {
      const card = el("div", "adm-card aci-card");
      card.appendChild(el("div", "aci-stage-tag", "Step 1 · Upload"));

      // Supplier picker
      const supRow = el("div", "aci-field");
      supRow.appendChild(el("label", "aci-label", "Supplier"));
      const sel = el("select", "adm-select");
      sel.disabled = suppliers.length === 0 || addingSupplier;
      const ph = el("option", undefined, suppliers.length ? "Select a supplier…" : "Loading suppliers…");
      ph.value = "";
      sel.appendChild(ph);
      for (const s of suppliers) {
        const o = el("option", undefined, s.name);
        o.value = s.id;
        if (s.id === supplierId) o.selected = true;
        sel.appendChild(o);
      }
      sel.addEventListener("change", () => {
        supplierId = sel.value;
        // Supplier changed — any staged upload no longer matches.
        upload = null; invalidateFromUpload();
        render();
      });
      supRow.appendChild(sel);

      // Add-new-supplier inline
      if (!addingSupplier) {
        const addLink = el("button", "aci-link", "+ Add new supplier");
        addLink.type = "button";
        addLink.addEventListener("click", () => { addingSupplier = true; render(); });
        supRow.appendChild(addLink);
      } else {
        const addBox = el("div", "aci-addbox");
        const inp = el("input", "adm-input");
        inp.type = "text";
        inp.placeholder = "New supplier name";
        inp.id = "aci-newsup";
        const create = el("button", "adm-btn", creatingSupplier ? "Creating…" : "Create");
        create.type = "button";
        create.disabled = creatingSupplier;
        create.addEventListener("click", () => void createSupplier(inp.value));
        const cancel = el("button", "adm-btn-ghost", "Cancel");
        cancel.type = "button";
        cancel.addEventListener("click", () => { addingSupplier = false; render(); });
        addBox.appendChild(inp);
        addBox.appendChild(create);
        addBox.appendChild(cancel);
        supRow.appendChild(addBox);
      }
      card.appendChild(supRow);

      // File drop
      const drop = el("div", "aci-drop");
      const paintDrop = (): void => {
        drop.replaceChildren();
        if (file) {
          drop.appendChild(el("div", "aci-file-name", file.name));
          drop.appendChild(el("div", "aci-file-meta", fmtBytes(file.size)));
          const clear = el("button", "adm-btn-ghost aci-clear", "Remove");
          clear.type = "button";
          clear.addEventListener("click", (e) => {
            e.stopPropagation();
            file = null; upload = null; invalidateFromUpload(); render();
          });
          drop.appendChild(clear);
        } else {
          drop.appendChild(el("div", "aci-drop-title", "Drop a CSV or Excel file here, or click to browse"));
          drop.appendChild(el("div", "aci-drop-hint", "Max 60 MB · .csv, .xlsx, .xls"));
        }
      };
      const fileInput = el("input");
      fileInput.type = "file";
      fileInput.accept = ".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      fileInput.hidden = true;
      const acceptFile = (f: File | undefined | null): void => {
        if (!f) return;
        if (!/\.(csv|xlsx|xls)$/i.test(f.name)) { ctx.showToast("File must be CSV or Excel (.csv, .xlsx, .xls)", "error"); return; }
        if (f.size > MAX_FILE_BYTES) { ctx.showToast(`File too large — max ${fmtBytes(MAX_FILE_BYTES)}`, "error"); return; }
        file = f; upload = null; invalidateFromUpload(); render();
      };
      drop.addEventListener("click", () => fileInput.click());
      fileInput.addEventListener("change", () => acceptFile(fileInput.files?.[0]));
      drop.addEventListener("dragover", (e) => { e.preventDefault(); drop.classList.add("is-drag"); });
      drop.addEventListener("dragleave", () => drop.classList.remove("is-drag"));
      drop.addEventListener("drop", (e) => {
        e.preventDefault(); drop.classList.remove("is-drag");
        acceptFile(e.dataTransfer?.files?.[0]);
      });
      paintDrop();
      card.appendChild(drop);
      card.appendChild(fileInput);

      // Upload action
      const actions = el("div", "aci-actions");
      const upBtn = el("button", "adm-btn", uploading ? "Reading file…" : "Upload & preview");
      upBtn.type = "button";
      upBtn.disabled = uploading || !file || !supplierId;
      upBtn.addEventListener("click", () => void runUpload());
      actions.appendChild(upBtn);
      if (!supplierId) actions.appendChild(el("span", "aci-hint", "Pick a supplier and a file to continue."));
      card.appendChild(actions);

      // Preview (after upload)
      if (upload) {
        if (upload.saved_mapping) {
          card.appendChild(el("div", "aci-banner is-info",
            "Saved mapping found for this supplier — review and confirm it on the next step."));
        }
        if (upload.skipped_title) {
          card.appendChild(el("div", "aci-banner is-info",
            `Skipped a title row — reading column headers from row ${upload.header_row}.`));
        }
        const meta = el("div", "aci-log");
        meta.appendChild(el("div", "aci-log-line",
          `${upload.headers.length} columns · ${upload.total_rows.toLocaleString()} data rows detected in ${upload.filename}.`));
        card.appendChild(meta);
        card.appendChild(previewTable(upload.headers, upload.preview_rows));

        const next = el("div", "aci-actions");
        const nextBtn = el("button", "adm-btn", "Next: map columns →");
        nextBtn.type = "button";
        nextBtn.addEventListener("click", () => {
          // Seed mapping from saved mapping (only for headers that still exist).
          if (Object.keys(mapping).length === 0 && upload?.saved_mapping) {
            const valid = new Set(upload.headers);
            for (const f of fields) {
              const col = upload.saved_mapping[f.key];
              mapping[f.key] = col && valid.has(col) ? col : SKIP;
            }
          }
          goStep(2);
        });
        next.appendChild(nextBtn);
        card.appendChild(next);
      }

      return card;
    }

    function previewTable(headers: string[], rows: string[][]): HTMLElement {
      const wrap = el("div", "aci-table-wrap");
      const table = el("table", "adm-table aci-table");
      const thead = el("thead");
      const htr = el("tr");
      for (const h of headers) htr.appendChild(el("th", undefined, h || "—"));
      thead.appendChild(htr);
      table.appendChild(thead);
      const tbody = el("tbody");
      for (const r of rows) {
        const tr = el("tr");
        for (let i = 0; i < headers.length; i++) {
          const v = r[i];
          tr.appendChild(el("td", undefined, v == null || v === "" ? "—" : String(v)));
        }
        tbody.appendChild(tr);
      }
      table.appendChild(tbody);
      wrap.appendChild(table);
      return wrap;
    }

    // ── Step 2 — Map ─────────────────────────────────────────────────────────
    function renderMap(): HTMLElement {
      const card = el("div", "adm-card aci-card");
      card.appendChild(el("div", "aci-stage-tag", "Step 2 · Map columns"));
      if (!upload) { card.appendChild(el("p", "aci-muted", "Upload a file first.")); return card; }

      card.appendChild(el("p", "aci-sub",
        "Match each TKOS field to a column in your file. Required fields are marked *."));

      const dups = duplicateColumns();

      const tableWrap = el("div", "aci-map");
      for (const f of fields) {
        const rowEl = el("div", "aci-map-row");
        const left = el("div", "aci-map-left");
        left.appendChild(el("span", "aci-map-field", f.label));
        const badge = el("span", f.required ? "aci-badge is-req" : "aci-badge", f.required ? "Required" : "Optional");
        left.appendChild(badge);
        rowEl.appendChild(left);

        const sel = el("select", "adm-select aci-map-select");
        const skipOpt = el("option", undefined, "— Skip this column —");
        skipOpt.value = SKIP;
        sel.appendChild(skipOpt);
        const current = mapping[f.key] ?? SKIP;
        for (const h of upload!.headers) {
          const o = el("option", undefined, h);
          o.value = h;
          if (h === current) o.selected = true;
          sel.appendChild(o);
        }
        if (current === SKIP) skipOpt.selected = true;
        if (current && dups.includes(current)) sel.classList.add("is-dup");
        sel.addEventListener("change", () => {
          mapping[f.key] = sel.value;
          invalidateFromMapping();
          render();
        });
        rowEl.appendChild(sel);
        tableWrap.appendChild(rowEl);
      }
      card.appendChild(tableWrap);

      if (dups.length) {
        card.appendChild(el("div", "aci-banner is-warn",
          `The same column is mapped to more than one field: ${dups.join(", ")}. Check this is intentional.`));
      }
      if (!requiredMapped()) {
        const missing = fields.filter((f) => f.required && !(mapping[f.key] || "").trim()).map((f) => f.label);
        card.appendChild(el("div", "aci-banner is-warn", `Map all required fields to continue: ${missing.join(", ")}.`));
      }

      // Save-mapping toggle
      const saveRow = el("label", "aci-check");
      const cb = el("input");
      cb.type = "checkbox";
      cb.checked = saveMapping;
      cb.addEventListener("change", () => { saveMapping = cb.checked; });
      saveRow.appendChild(cb);
      saveRow.appendChild(el("span", undefined, "Save this mapping for future uploads of this supplier"));
      card.appendChild(saveRow);

      const actions = el("div", "aci-actions");
      const back = el("button", "adm-btn-ghost", "← Back");
      back.type = "button";
      back.addEventListener("click", () => goStep(1));
      actions.appendChild(back);
      const next = el("button", "adm-btn", validating ? "Validating…" : "Validate →");
      next.type = "button";
      next.disabled = !requiredMapped() || validating;
      next.addEventListener("click", () => void runValidate());
      actions.appendChild(next);
      card.appendChild(actions);

      return card;
    }

    // ── Step 3 — Validate ────────────────────────────────────────────────────
    function renderValidate(): HTMLElement {
      const card = el("div", "adm-card aci-card");
      card.appendChild(el("div", "aci-stage-tag", "Step 3 · Validate"));

      if (validating || !validation) {
        card.appendChild(el("p", "aci-muted", validating ? "Validating rows…" : "No validation yet."));
        return card;
      }
      const v = validation;
      const c = v.counts;

      // Summary banner
      const summary = el("div", "aci-summary");
      const pill = (cls: string, icon: string, text: string): void => {
        const p = el("div", `aci-spill ${cls}`);
        p.appendChild(el("span", "aci-spill-icon", icon));
        p.appendChild(el("span", undefined, text));
        summary.appendChild(p);
      };
      pill("is-ok", "✓", `${c.valid_rows.toLocaleString()} rows valid`);
      pill("is-warn", "⚠", `${c.warning_rows.toLocaleString()} with warnings`);
      pill("is-err", "✕", `${c.error_rows.toLocaleString()} with errors`);
      card.appendChild(summary);

      // Counts line
      const counts = el("div", "aci-counts");
      counts.appendChild(el("div", "aci-count-line", `File contains ${c.total_rows.toLocaleString()} rows`));
      counts.appendChild(el("div", "aci-count-line is-ok", `Ready to import: ${c.ready_to_import.toLocaleString()}`));
      counts.appendChild(el("div", "aci-count-line is-muted", `Will be skipped: ${c.skipped_rows.toLocaleString()} (missing required fields or invalid price)`));
      card.appendChild(counts);

      // Price-cleaning is applied to the preview below, exactly as it will be
      // imported (the ¬ / £ / comma artifacts are stripped — not left raw).
      if (c.cleaned_price_cells > 0) {
        card.appendChild(el("div", "aci-banner is-info",
          `Auto-cleaned currency formatting (¬, £, commas) from ${c.cleaned_price_cells.toLocaleString()} price values. The preview below shows the cleaned figures that will be imported.`));
      }

      // Issues table
      if (v.issues.length) {
        card.appendChild(el("div", "aci-subhead", `Row issues (${v.issues.length}${v.issues.length >= 500 ? "+" : ""})`));
        const wrap = el("div", "aci-table-wrap");
        const t = el("table", "adm-table aci-table");
        const thead = el("thead");
        const htr = el("tr");
        ["Row", "Field", "Value", "Issue"].forEach((h) => htr.appendChild(el("th", undefined, h)));
        thead.appendChild(htr);
        t.appendChild(thead);
        const tbody = el("tbody");
        for (const is of v.issues) {
          const tr = el("tr", is.severity === "error" ? "is-err-row" : "is-warn-row");
          tr.appendChild(el("td", undefined, String(is.row)));
          tr.appendChild(el("td", undefined, is.field));
          tr.appendChild(el("td", undefined, is.value === "" ? "(empty)" : is.value));
          tr.appendChild(el("td", undefined, is.issue));
          tbody.appendChild(tr);
        }
        t.appendChild(tbody);
        wrap.appendChild(t);
        card.appendChild(wrap);
      } else {
        card.appendChild(el("div", "aci-banner is-info", "No row-level issues found."));
      }

      // Cleaned sample
      if (v.sample.length) {
        const det = el("details", "aci-sample");
        det.open = true;   // show the cleaned figures up-front (WYSIWYG with import)
        det.appendChild(el("summary", undefined, `Preview first ${v.sample.length} cleaned rows`));
        const wrap = el("div", "aci-table-wrap");
        const t = el("table", "adm-table aci-table");
        const thead = el("thead");
        const htr = el("tr");
        ["Code", "Description", "Range", "List", "Net", "PTO", "Disc"].forEach((h) => htr.appendChild(el("th", undefined, h)));
        thead.appendChild(htr);
        t.appendChild(thead);
        const tbody = el("tbody");
        for (const s of v.sample) {
          const tr = el("tr");
          tr.appendChild(el("td", undefined, s.product_code));
          tr.appendChild(el("td", undefined, s.description));
          tr.appendChild(el("td", undefined, s.range || "—"));
          tr.appendChild(el("td", undefined, gbp(s.list_price)));
          tr.appendChild(el("td", undefined, gbp(s.net_price)));
          tr.appendChild(el("td", undefined, gbp(s.pto_price)));
          tr.appendChild(el("td", undefined, s.discount_code || "—"));
          tbody.appendChild(tr);
        }
        t.appendChild(tbody);
        wrap.appendChild(t);
        det.appendChild(wrap);
        card.appendChild(det);
      }

      // Soft-removal opt-in
      const deRow = el("label", "aci-check");
      const cb = el("input");
      cb.type = "checkbox";
      cb.checked = deactivateAbsent;
      cb.addEventListener("change", () => { deactivateAbsent = cb.checked; });
      deRow.appendChild(cb);
      deRow.appendChild(el("span", undefined,
        "Deactivate this supplier's previously-imported products that are NOT in this file (hides discontinued lines). Recoverable by re-importing the full list."));
      card.appendChild(deRow);

      // Actions
      const actions = el("div", "aci-actions");
      const back = el("button", "adm-btn-ghost", "← Back to mapping");
      back.type = "button";
      back.addEventListener("click", () => goStep(2));
      actions.appendChild(back);
      const importBtn = el("button", "adm-btn", `Import ${c.ready_to_import.toLocaleString()} Products`);
      importBtn.type = "button";
      importBtn.disabled = c.ready_to_import === 0 || running;
      importBtn.addEventListener("click", () => void runFull());
      actions.appendChild(importBtn);
      card.appendChild(actions);

      return card;
    }

    // ── Step 4 — Import & result ─────────────────────────────────────────────
    function renderImport(): HTMLElement {
      const card = el("div", "adm-card aci-card");
      card.appendChild(el("div", "aci-stage-tag", "Step 4 · Import"));

      if (running) {
        const prog = el("div", "aci-progress");
        prog.appendChild(el("div", "aci-spinner"));
        prog.appendChild(el("div", undefined, "Importing products… this can take a few seconds for a large file."));
        card.appendChild(prog);
        return card;
      }

      if (!runResult) { card.appendChild(el("p", "aci-muted", "Nothing imported yet.")); return card; }

      const r = runResult;
      const rc = r.counts;
      const cls = r.status === "success" ? "is-ok" : (r.status === "partial" ? "is-warn" : "is-err");
      const result = el("div", `aci-result ${cls}`);
      result.appendChild(el("div", "aci-result-title",
        r.status === "failed" ? "✕ Import failed" : (r.status === "partial" ? "⚠ Import completed with errors" : "✓ Import complete")));

      const grid = el("div", "aci-result-grid");
      const stat = (label: string, value: string): void => {
        const cell = el("div", "aci-stat");
        cell.appendChild(el("div", "aci-stat-val", value));
        cell.appendChild(el("div", "aci-stat-label", label));
        grid.appendChild(cell);
      };
      stat("Added", rc.inserted_rows.toLocaleString());
      stat("Updated", rc.updated_rows.toLocaleString());
      stat("Skipped (errors)", rc.error_rows.toLocaleString());
      if (rc.deactivated_rows) stat("Deactivated", rc.deactivated_rows.toLocaleString());
      result.appendChild(grid);

      const meta = el("div", "aci-result-meta");
      meta.appendChild(el("div", undefined, `Supplier: ${r.supplier.name}`));
      if (upload) meta.appendChild(el("div", undefined, `File: ${upload.filename}`));
      meta.appendChild(el("div", undefined, `Duration: ${(r.duration_ms / 1000).toFixed(1)}s`));
      result.appendChild(meta);

      if (r.errors && r.errors.length) {
        const det = el("details", "aci-errors");
        det.appendChild(el("summary", undefined, `View error log (${rc.error_rows})`));
        const ul = el("ul");
        for (const e of r.errors) ul.appendChild(el("li", undefined, `Row ${e.row}: ${e.reason}`));
        det.appendChild(ul);
        result.appendChild(det);
      }
      card.appendChild(result);

      const actions = el("div", "aci-actions");
      const again = el("button", "adm-btn", "Import another file");
      again.type = "button";
      again.addEventListener("click", () => resetAll());
      actions.appendChild(again);
      const viewCat = el("button", "adm-btn-ghost", "Done");
      viewCat.type = "button";
      viewCat.addEventListener("click", () => ctx.navigate("catalogue"));
      actions.appendChild(viewCat);
      card.appendChild(actions);

      return card;
    }

    // ── Actions ──────────────────────────────────────────────────────────────
    async function createSupplier(name: string): Promise<void> {
      const n = name.trim();
      if (!n) { ctx.showToast("Enter a supplier name", "error"); return; }
      creatingSupplier = true; render();
      try {
        const resp = await api<{ ok: boolean; supplier: SupplierOpt }>("/admin/catalogue/import/supplier", {
          method: "POST", signal: ac.signal, body: JSON.stringify({ name: n }),
        });
        if (!suppliers.find((s) => s.id === resp.supplier.id)) suppliers.push(resp.supplier);
        suppliers.sort((a, b) => a.name.localeCompare(b.name));
        supplierId = resp.supplier.id;
        addingSupplier = false;
        upload = null; invalidateFromUpload();
        ctx.showToast(`Supplier "${resp.supplier.name}" ready`, "success");
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        ctx.showToast(`Could not create supplier: ${(err as Error).message}`, "error");
      } finally {
        creatingSupplier = false; render();
      }
    }

    async function runUpload(): Promise<void> {
      if (uploading || !file || !supplierId) return;
      uploading = true; upload = null; invalidateFromUpload(); render();
      try {
        const fd = new FormData();
        fd.append("supplier_id", supplierId);
        fd.append("file", file, file.name);
        const res = await fetch("/api/admin/catalogue/import/upload", {
          method: "POST", credentials: "include", body: fd, signal: ac.signal,
        });
        if (!res.ok) throw new Error(await errMessage(res));
        upload = (await res.json()) as UploadResp;
        ctx.showToast(`Read ${upload.total_rows.toLocaleString()} rows`, "success");
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        ctx.showToast(`Upload failed: ${(err as Error).message}`, "error");
      } finally {
        uploading = false; render();
      }
    }

    async function runValidate(): Promise<void> {
      if (!upload || validating || !requiredMapped()) return;
      validating = true; validation = null;
      goStep(3);
      try {
        validation = await api<ValidateResp>("/admin/catalogue/import/validate", {
          method: "POST", signal: ac.signal,
          body: JSON.stringify({ supplier_id: supplierId, file_ref: upload.file_ref, mapping }),
        });
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        ctx.showToast(`Validation failed: ${(err as Error).message}`, "error");
        goStep(2);
      } finally {
        validating = false; render();
      }
    }

    async function runFull(): Promise<void> {
      if (!upload || running || !validation || validation.counts.ready_to_import === 0) return;
      running = true; runResult = null;
      goStep(4);
      try {
        runResult = await api<RunResp>("/admin/catalogue/import/run", {
          method: "POST", signal: ac.signal,
          body: JSON.stringify({
            supplier_id: supplierId, file_ref: upload.file_ref, mapping,
            save_mapping: saveMapping, deactivate_absent: deactivateAbsent,
          }),
        });
        const rc = runResult.counts;
        ctx.showToast(`Imported ${rc.imported_rows.toLocaleString()} products (${rc.inserted_rows} new, ${rc.updated_rows} updated)`,
          runResult.status === "failed" ? "error" : "success");
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        ctx.showToast(`Import failed: ${(err as Error).message}`, "error");
        // The staged file is consumed by a successful run; on failure it may have
        // expired — send the user back to re-upload.
        goStep(1); upload = null; invalidateFromUpload();
      } finally {
        running = false; render();
      }
    }

    function resetAll(): void {
      file = null; upload = null; mapping = {}; validation = null; runResult = null;
      deactivateAbsent = false; saveMapping = true; step = 1; maxStep = 1;
      render();
    }

    // ── Init ───────────────────────────────────────────────────────────────────
    render();
    void (async () => {
      try {
        const [supResp, fieldResp] = await Promise.all([
          api<{ suppliers: SupplierOpt[] }>("/admin/catalogue/import/suppliers", { signal: ac.signal }),
          api<{ fields: FieldDef[] }>("/admin/catalogue/import/fields", { signal: ac.signal }).catch(() => ({ fields: FALLBACK_FIELDS })),
        ]);
        suppliers = supResp.suppliers || [];
        if (fieldResp.fields && fieldResp.fields.length) fields = fieldResp.fields;
        render();
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        ctx.showToast("Failed to load suppliers", "error");
      }
    })();

    return () => { ac.abort(); };
  },
};

registerPage(page);
